import React, { useMemo } from 'react';
import { Row, Col, Card, Statistic, Typography, List, Avatar, Divider, Tag, theme } from 'antd';
import { GlobalOutlined, FileTextOutlined, EyeOutlined, CheckCircleOutlined, PlayCircleOutlined, CloseCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery } from 'react-query';
import { useTheme } from '../contexts/ThemeContext';
import { websiteApi } from '../services/api';
import { videoApi } from '../services/videoApi';
import { Website } from '../types/website';

const { Title, Text } = Typography;
const { useToken } = theme;

const Dashboard: React.FC = () => {
  const { theme: currentTheme } = useTheme();
  const { token } = useToken();
  const isDark = currentTheme === 'dark';

  const { data: websitesResponse, isLoading: websitesLoading } = useQuery(
    'websites',
    () => websiteApi.getAll({ limit: 1000 }) // Get all websites for dashboard stats
  );
  const websites: Website[] = websitesResponse?.data || [];
  const { data: videos = [] } = useQuery('videos', () => videoApi.getAll());
  const { data: videoStats, isLoading: statsLoading } = useQuery('video-stats', () => videoApi.getStats());

  const totalWebsites = websites.length;
  const activeWebsites = websites.filter((w: Website) => w.isActive).length;
  const totalFiles = websites.reduce((acc: number, w: Website) => acc + (w.status === 'active' ? 1 : 0), 0);
  const totalVideos = videos.length;
  const publishedVideos = videos.filter((v: any) => v.isPublished).length;
  const totalViews = videoStats?.totalViews || 0;
  
  // Google Indexed statistics
  const indexedWebsites = websites.filter((w: Website) => w.isIndexed === true).length;
  const notIndexedWebsites = websites.filter((w: Website) => w.isIndexed === false || w.isIndexed === null || w.isIndexed === undefined).length;

  const recentWebsites = websites
    .sort((a: Website, b: Website) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  // Theme-aware gradients and colors
  const gradients = useMemo(() => {
    if (isDark) {
      return {
        purple: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        green: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
        pink: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        blue: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        purpleLight: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
        greenLight: 'linear-gradient(135deg, rgba(17, 153, 142, 0.2) 0%, rgba(56, 239, 125, 0.2) 100%)',
        pinkLight: 'linear-gradient(135deg, rgba(240, 147, 251, 0.2) 0%, rgba(245, 87, 108, 0.2) 100%)',
        blueLight: 'linear-gradient(135deg, rgba(79, 172, 254, 0.2) 0%, rgba(0, 242, 254, 0.2) 100%)',
      };
    } else {
      return {
        purple: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        green: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
        pink: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        blue: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        purpleLight: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
        greenLight: 'linear-gradient(135deg, rgba(17, 153, 142, 0.1) 0%, rgba(56, 239, 125, 0.1) 100%)',
        pinkLight: 'linear-gradient(135deg, rgba(240, 147, 251, 0.1) 0%, rgba(245, 87, 108, 0.1) 100%)',
        blueLight: 'linear-gradient(135deg, rgba(79, 172, 254, 0.1) 0%, rgba(0, 242, 254, 0.1) 100%)',
      };
    }
  }, [isDark]);

  const cardStyle = {
    borderRadius: '12px',
    boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
    transition: 'all 0.3s ease',
    border: 'none',
    overflow: 'hidden',
    background: token.colorBgContainer,
  };

  return (
    <div style={{ padding: '24px', background: token.colorBgLayout, minHeight: '100vh' }}>
      <div style={{ marginBottom: '32px' }}>
        <Title level={2} style={{ margin: 0, fontWeight: 600, color: token.colorText }}>
          Dashboard
        </Title>
        <Text type="secondary" style={{ fontSize: '14px', color: token.colorTextSecondary }}>
          Overview of your websites and content statistics
        </Text>
      </div>
      
      <Row gutter={[20, 20]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card
            styles={{
              body: {
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '24px',
                color: 'white'
              }
            }}
          >
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: 500 }}>Total Websites</span>}
              value={totalWebsites}
              prefix={<GlobalOutlined style={{ fontSize: '24px', color: 'rgba(255,255,255,0.9)' }} />}
              valueStyle={{ color: '#fff', fontSize: '32px', fontWeight: 700 }}
              loading={websitesLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card
            styles={{
              body: {
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                padding: '24px',
                color: 'white'
              }
            }}
          >
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: 500 }}>Active Websites</span>}
              value={activeWebsites}
              prefix={<CheckCircleOutlined style={{ fontSize: '24px', color: 'rgba(255,255,255,0.9)' }} />}
              valueStyle={{ color: '#fff', fontSize: '32px', fontWeight: 700 }}
              loading={websitesLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card
            styles={{
              body: {
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                padding: '24px',
                color: 'white'
              }
            }}
          >
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: 500 }}>Total Files</span>}
              value={totalFiles}
              prefix={<FileTextOutlined style={{ fontSize: '24px', color: 'rgba(255,255,255,0.9)' }} />}
              valueStyle={{ color: '#fff', fontSize: '32px', fontWeight: 700 }}
              loading={websitesLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card
            styles={{
              body: {
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                padding: '24px',
                color: 'white'
              }
            }}
          >
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: 500 }}>Views Today</span>}
              value={1128}
              prefix={<EyeOutlined style={{ fontSize: '24px', color: 'rgba(255,255,255,0.9)' }} />}
              valueStyle={{ color: '#fff', fontSize: '32px', fontWeight: 700 }}
              loading={websitesLoading}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[20, 20]}>
        <Col xs={24} lg={8}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <SearchOutlined style={{ color: token.colorInfo, fontSize: '18px' }} />
                <span style={{ fontWeight: 600, fontSize: '16px', color: token.colorText }}>Google Indexed</span>
              </div>
            }
            loading={websitesLoading}
            style={cardStyle}
            styles={{
              header: {
                background: gradients.purpleLight,
                borderBottom: `2px solid ${isDark ? 'rgba(102, 126, 234, 0.4)' : 'rgba(102, 126, 234, 0.3)'}`,
                padding: '16px 24px'
              },
              body: {
                padding: '24px',
                background: token.colorBgContainer
              }
            }}
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div style={{ 
                  background: gradients.greenLight,
                  padding: '16px',
                  borderRadius: '8px',
                  border: `1px solid ${isDark ? 'rgba(17, 153, 142, 0.4)' : 'rgba(17, 153, 142, 0.3)'}`
                }}>
                  <Statistic
                    title={<span style={{ fontSize: '13px', color: token.colorTextSecondary }}>Indexed</span>}
                    value={indexedWebsites}
                    prefix={<CheckCircleOutlined style={{ color: token.colorSuccess, fontSize: '20px' }} />}
                    valueStyle={{ color: token.colorSuccess, fontSize: '28px', fontWeight: 600 }}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ 
                  background: gradients.pinkLight,
                  padding: '16px',
                  borderRadius: '8px',
                  border: `1px solid ${isDark ? 'rgba(245, 87, 108, 0.4)' : 'rgba(245, 87, 108, 0.3)'}`
                }}>
                  <Statistic
                    title={<span style={{ fontSize: '13px', color: token.colorTextSecondary }}>Not Indexed</span>}
                    value={notIndexedWebsites}
                    prefix={<CloseCircleOutlined style={{ color: token.colorError, fontSize: '20px' }} />}
                    valueStyle={{ color: token.colorError, fontSize: '28px', fontWeight: 600 }}
                  />
                </div>
              </Col>
            </Row>
            <Divider style={{ margin: '16px 0' }} />
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Total: <strong>{indexedWebsites + notIndexedWebsites}</strong> websites
              </Text>
            </div>
          </Card>
        </Col>
        
        <Col xs={24} lg={8}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <GlobalOutlined style={{ color: token.colorInfo, fontSize: '18px' }} />
                <span style={{ fontWeight: 600, fontSize: '16px', color: token.colorText }}>Recent Websites</span>
              </div>
            }
            loading={websitesLoading}
            style={cardStyle}
            styles={{
              header: {
                background: gradients.blueLight,
                borderBottom: `2px solid ${isDark ? 'rgba(79, 172, 254, 0.4)' : 'rgba(79, 172, 254, 0.3)'}`,
                padding: '16px 24px'
              },
              body: {
                padding: '16px',
                background: token.colorBgContainer
              }
            }}
          >
            <List
              itemLayout="horizontal"
              dataSource={recentWebsites}
              renderItem={(website, index) => (
                <List.Item
                  style={{
                    padding: '12px 0',
                    borderBottom: index < recentWebsites.length - 1 ? `1px solid ${token.colorBorderSecondary}` : 'none',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = token.colorFill;
                    e.currentTarget.style.paddingLeft = '8px';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.paddingLeft = '0';
                  }}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar 
                        icon={<GlobalOutlined />} 
                        style={{ 
                          background: gradients.purple,
                          border: `2px solid ${token.colorBgContainer}`,
                          boxShadow: isDark ? '0 2px 4px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)'
                        }} 
                      />
                    }
                    title={
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: 500 }}>{website.name}</span>
                        {website.isActive && (
                          <Tag color="success" style={{ margin: 0, fontSize: '11px' }}>Active</Tag>
                        )}
                      </div>
                    }
                    description={
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {website.domain}
                        </Text>
                        <br />
                        <Tag color="blue" style={{ marginTop: '4px', fontSize: '11px' }}>
                          {website.type}
                        </Tag>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
        
        <Col xs={24} lg={8}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <PlayCircleOutlined style={{ color: token.colorInfo, fontSize: '18px' }} />
                <span style={{ fontWeight: 600, fontSize: '16px', color: token.colorText }}>Video Statistics</span>
              </div>
            }
            loading={statsLoading}
            style={cardStyle}
            styles={{
              header: {
                background: gradients.pinkLight,
                borderBottom: `2px solid ${isDark ? 'rgba(240, 147, 251, 0.4)' : 'rgba(240, 147, 251, 0.3)'}`,
                padding: '16px 24px'
              },
              body: {
                padding: '24px',
                background: token.colorBgContainer
              }
            }}
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div style={{ 
                  background: gradients.purpleLight,
                  padding: '16px',
                  borderRadius: '8px',
                  border: `1px solid ${isDark ? 'rgba(102, 126, 234, 0.4)' : 'rgba(102, 126, 234, 0.3)'}`,
                  textAlign: 'center'
                }}>
                  <Statistic
                    title={<span style={{ fontSize: '13px', color: token.colorTextSecondary }}>Total Videos</span>}
                    value={totalVideos}
                    prefix={<PlayCircleOutlined style={{ color: '#667eea', fontSize: '20px' }} />}
                    valueStyle={{ color: '#667eea', fontSize: '24px', fontWeight: 600 }}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ 
                  background: gradients.greenLight,
                  padding: '16px',
                  borderRadius: '8px',
                  border: `1px solid ${isDark ? 'rgba(17, 153, 142, 0.4)' : 'rgba(17, 153, 142, 0.3)'}`,
                  textAlign: 'center'
                }}>
                  <Statistic
                    title={<span style={{ fontSize: '13px', color: token.colorTextSecondary }}>Published</span>}
                    value={publishedVideos}
                    valueStyle={{ color: token.colorSuccess, fontSize: '24px', fontWeight: 600 }}
                  />
                </div>
              </Col>
            </Row>
            <Divider style={{ margin: '16px 0', borderColor: token.colorBorderSecondary }} />
            <div style={{ 
              background: gradients.blueLight,
              padding: '16px',
              borderRadius: '8px',
              border: `1px solid ${isDark ? 'rgba(79, 172, 254, 0.4)' : 'rgba(79, 172, 254, 0.3)'}`,
              textAlign: 'center'
            }}>
              <Statistic
                title={<span style={{ fontSize: '13px', color: token.colorTextSecondary }}>Total Views</span>}
                value={totalViews}
                prefix={<EyeOutlined style={{ color: token.colorInfo, fontSize: '20px' }} />}
                valueStyle={{ color: token.colorInfo, fontSize: '28px', fontWeight: 600 }}
              />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
